import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { css } from 'aphrodite';
import * as React from 'react';
import { BulletIcon } from '../../components/svgs/icons/BulletIcon';
import { CheckmarkIcon } from '../../components/svgs/icons/CheckmarkIcon';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { XIcon, XIconNew } from '../../components/svgs/icons/XIcon';
import { brandPrimary } from '../../styles/colors';
import { InputGroupV2 as InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '../InputGroup';
import { s } from './styles';

// Root - Groups all parts of the combobox
function Combobox({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Root>) {
	return <ComboboxPrimitive.Root data-slot='combobox' {...props} />;
}

// Value - Displays the selected value
function ComboboxValue({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Value>) {
	return <ComboboxPrimitive.Value data-slot='combobox-value' {...props} />;
}

function ComboboxInput({
	styles = [],
	children,
	disabled = false,
	showTrigger = true,
	showClear = false,
	triggerAriaLabel = 'Show options',
	clearAriaLabel = 'Clear selection',
	...props
}: AphroditeStyled<ComboboxPrimitive.Input.Props> & {
	showTrigger?: boolean;
	showClear?: boolean;
	triggerAriaLabel?: string;
	clearAriaLabel?: string;
}) {
	return (
		<InputGroup styles={[s.inputGroup, ...styles]}>
			<ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
			<InputGroupAddon align='inlineEnd'>
				{showTrigger && (
					<InputGroupButton size='iconXs' variant='ghost' data-slot='input-group-button' disabled={disabled} asChild>
						<ComboboxTrigger aria-label={triggerAriaLabel} />
					</InputGroupButton>
				)}
				{showClear && <ComboboxClear aria-label={clearAriaLabel} disabled={disabled} />}
			</InputGroupAddon>
			{children}
		</InputGroup>
	);
}

// Status - Displays the status of the combobox
function ComboboxStatus({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Status>>) {
	return <ComboboxPrimitive.Status data-slot='combobox-status' className={css(s.status, ...styles)} {...props} />;
}

// Portal - A portal element that moves the popup to a different part of the DOM
function ComboboxPortal({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Portal>) {
	return <ComboboxPrimitive.Portal data-slot='combobox-portal' {...props} />;
}

export interface ComboboxContentProps extends AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Popup>> {
	align?: ComboboxPrimitive.Positioner.Props['align'];
	sideOffset?: ComboboxPrimitive.Positioner.Props['sideOffset'];
	alignOffset?: ComboboxPrimitive.Positioner.Props['alignOffset'];
	side?: ComboboxPrimitive.Positioner.Props['side'];
	showBackdrop?: boolean;
	anchor?: React.ComponentProps<typeof ComboboxPrimitive.Positioner>['anchor'];
}

function ComboboxContent({
	styles = [],
	children,
	align = 'start',
	sideOffset = 4,
	alignOffset = 0,
	side = 'bottom',
	anchor,
	...props
}: ComboboxContentProps) {
	return (
		<ComboboxPortal>
			<ComboboxPositioner align={align} sideOffset={sideOffset} alignOffset={alignOffset} side={side} anchor={anchor}>
				<ComboboxPopup
					data-slot='combobox-content'
					data-chips={!!anchor}
					styles={[s.comboboxPopup, ...styles]}
					{...props}
				>
					{children}
				</ComboboxPopup>
			</ComboboxPositioner>
		</ComboboxPortal>
	);
}

// Positioner - Positions the combobox popup against the input
function ComboboxPositioner({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Positioner>>) {
	return (
		<ComboboxPrimitive.Positioner data-slot='combobox-positioner' className={css(s.positioner, ...styles)} {...props} />
	);
}

// Popup - A container for the combobox options
function ComboboxPopup({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Popup>>) {
	return <ComboboxPrimitive.Popup data-slot='combobox-popup' className={css(s.popup, ...styles)} {...props} />;
}

// List - A container for the combobox options
function ComboboxList({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.List>>) {
	return <ComboboxPrimitive.List data-slot='combobox-list' className={css(s.list, ...styles)} {...props} />;
}

// Collection - A collection of combobox items
function ComboboxCollection({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Collection>) {
	return <ComboboxPrimitive.Collection data-slot='combobox-collection' {...props} />;
}

// Row - A row container for combobox items
function ComboboxRow({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Row>>) {
	return <ComboboxPrimitive.Row data-slot='combobox-row' className={css(s.row, ...styles)} {...props} />;
}

// Item - An individual selectable option in the combobox
function ComboboxItem({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Item>>) {
	return <ComboboxPrimitive.Item data-slot='combobox-item' className={css(s.item, ...styles)} {...props} />;
}

// ItemIndicator - An indicator for selected items
function ComboboxItemIndicator({
	styles = [],
	children,
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.ItemIndicator>>) {
	return (
		<ComboboxPrimitive.ItemIndicator
			data-slot='combobox-item-indicator'
			className={css(s.itemIndicator, ...styles)}
			{...props}
		>
			{children ? children : <CheckmarkIcon fillColor={brandPrimary} />}
		</ComboboxPrimitive.ItemIndicator>
	);
}

function ComboboxItemCheckboxIndicator({
	styles = [],
	children,
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.ItemIndicator>>) {
	return (
		<div className={css(s.checkBoxItemCheckWrapper)}>
			<ComboboxPrimitive.ItemIndicator
				data-slot='combobox-item-checkbox-indicator'
				className={css(s.checkBoxItemIndicator, ...styles)}
				{...props}
			>
				{children ? children : <CheckmarkIcon fillColor={brandPrimary} />}
			</ComboboxPrimitive.ItemIndicator>
		</div>
	);
}

function ComboboxItemRadioIndicator({
	styles = [],
	children,
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.ItemIndicator>>) {
	return (
		<div className={css(s.radioItemCheckWrapper)}>
			<ComboboxPrimitive.ItemIndicator
				data-slot='combobox-item-radio-indicator'
				className={css(s.radioItemCheckIndicator, ...styles)}
				{...props}
			>
				{children ? children : <BulletIcon fillColor={brandPrimary} />}
			</ComboboxPrimitive.ItemIndicator>
		</div>
	);
}

// Group - Groups related combobox items with the corresponding label
function ComboboxGroup({ ...props }: React.ComponentProps<typeof ComboboxPrimitive.Group>) {
	return <ComboboxPrimitive.Group data-slot='combobox-group' {...props} />;
}

// GroupLabel - An accessible label that is automatically associated with its parent group
function ComboboxGroupLabel({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.GroupLabel>>) {
	return (
		<ComboboxPrimitive.GroupLabel
			data-slot='combobox-group-label'
			className={css(s.groupLabel, ...styles)}
			{...props}
		/>
	);
}

// Empty - A component to display when no options are available
function ComboboxEmpty({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Empty>>) {
	return <ComboboxPrimitive.Empty data-slot='combobox-empty' className={css(s.empty, ...styles)} {...props} />;
}

// Clear - A button to clear the input value
function ComboboxClear({
	styles = [],
	children,
	'aria-label': ariaLabel = 'Clear selection',
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Clear>>) {
	return (
		<ComboboxPrimitive.Clear
			data-slot='combobox-clear'
			aria-label={ariaLabel}
			{...props}
			render={p => (
				<InputGroupButton variant='ghost' size='iconXs' styles={styles} {...p}>
					{children ?? <XIconNew style={{ pointerEvents: 'none' }} />}
				</InputGroupButton>
			)}
		/>
	);
}

// Trigger - A button that opens the combobox
function ComboboxTrigger({
	styles = [],
	children,
	'aria-label': ariaLabel,
	...props
}: AphroditeStyled<ComboboxPrimitive.Trigger.Props>) {
	return (
		<ComboboxPrimitive.Trigger
			data-slot='combobox-trigger'
			className={css(s.trigger, ...styles)}
			aria-label={ariaLabel ?? (children ? undefined : 'Show options')}
			{...props}
		>
			{children}
			<ChevronDown className={css(s.triggerChevron)} />
		</ComboboxPrimitive.Trigger>
	);
}

// Chips - A container for selected items as chips (for multi-select)
const ComboboxChips = React.forwardRef<
	React.ElementRef<typeof ComboboxPrimitive.Chips>,
	AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Chips>>
>(({ styles = [], ...props }, ref) => {
	return (
		<ComboboxPrimitive.Chips data-slot='combobox-chips' className={css(s.chips, ...styles)} ref={ref} {...props} />
	);
});

ComboboxChips.displayName = 'ComboboxChips';

// Chip - An individual chip representing a selected item
function ComboboxChip({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Chip>>) {
	return <ComboboxPrimitive.Chip data-slot='combobox-chip' className={css(s.chip, ...styles)} {...props} />;
}

function ComboboxChipsInput({ styles = [], ...props }: AphroditeStyled<ComboboxPrimitive.Input.Props>) {
	return (
		<ComboboxPrimitive.Input data-slot='combobox-chip-input' className={css(s.chipsInput, ...styles)} {...props} />
	);
}

// ChipRemove - A button to remove a chip
function ComboboxChipRemove({
	styles = [],
	children,
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.ChipRemove>>) {
	return (
		<ComboboxPrimitive.ChipRemove data-slot='combobox-chip-remove' className={css(s.chipRemove, ...styles)} {...props}>
			{children ? children : <XIcon width={8} height={8} />}
		</ComboboxPrimitive.ChipRemove>
	);
}

// Separator - A separator element accessible to screen readers
function ComboboxSeparator({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof ComboboxPrimitive.Separator>>) {
	return (
		<ComboboxPrimitive.Separator data-slot='combobox-separator' className={css(s.separator, ...styles)} {...props} />
	);
}

function useComboboxAnchor() {
	return React.useRef<HTMLDivElement | null>(null);
}

export {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxClear,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxItemIndicator,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxPopup,
	ComboboxPortal,
	ComboboxRow,
	ComboboxSeparator,
	ComboboxStatus,
	ComboboxTrigger,
	ComboboxValue,
	useComboboxAnchor,
};
